import css from './style.module.scss';
import { useEffect, useMemo, useRef, useState } from 'react';
import Button from '../Button';
import { AnimatePresence, motion } from 'framer-motion';
import getScrollParent from '../../util/getScrollParent';
import { Emit, Events, Listen } from '../../util/Events';
import cls from '../../util/cls';

const Checkbox = ({ isStatic = false, name, label, value, checked, onChange = null, className }) => (
	<motion.label
		className={cls(css.checkbox, className)}
		initial={isStatic ? {} : { height: 0, opacity: 0 }}
		animate={isStatic ? {} : { height: 'auto', opacity: 1 }}
		exit={isStatic ? {} : { height: 0, opacity: 0 }}
	>
		<span>
			<input
				type="checkbox"
				name={name}
				value={value}
				onChange={onChange}
				checked={checked}
			/>
			<span>{label}</span>
		</span>
	</motion.label>
);

export default function Checkboxes ({
	label = null,
	name,
	overflowLimit = 4,
	options = [],
	checked = null,
	defaultShowAll = false,
}) {
	const self = useRef();
	const [showAll, setShowAll] = useState(defaultShowAll);
	const [_checked, setChecked] = useState(checked ?? []);

	const scrollParent = useMemo(() => {
		if (!process.browser) return null;
		if (!self.current) return document.documentElement;
		return getScrollParent(self.current);
	}, [self]);

	const { visible, hidden } = useMemo(() => {
		return {
			visible: options.slice(0, overflowLimit).filter(Boolean),
			hidden: options.slice(overflowLimit).filter(Boolean),
		};
	}, [overflowLimit, options]);

	useEffect(() => {
		setChecked(checked ?? []);
	}, [checked]);

	useEffect(() => Listen(Events.ClearCheckboxes, _name => {
		if (name === _name) {
			setChecked([]);
			Emit(Events.CheckboxesChange, name, []);
		}
	}), [name]);

	const onShowAllClick = () => {
		if (scrollParent !== document.documentElement)
			scrollParent.scrollTo({ top: 0, behavior: 'smooth' });

		if (showAll) {
			setTimeout(() => {
				setShowAll(false);
			}, 350);
		} else setShowAll(true);
	};

	const onChange = e => {
		const { value, checked } = e.target;
		setChecked(prev => {
			const next = [...prev];

			if (checked) next.push(+value);
			else {
				const i = next.indexOf(+value);
				next.splice(i, 1);
			}

			// Delay this to allow react to update first
			requestAnimationFrame(
				() => Emit(Events.CheckboxesChange, name, next)
			);

			return next;
		});
	};

	// TODO: When showing fewer keep the selected values in the visible block,
	//  even if they go over the overflow limit

	return (
		<div className={css.checkboxes} ref={self}>
			{visible.map(({ label, value, isEmpty = false }) => (
				<Checkbox
					name={name}
					label={label}
					value={+value}
					key={value}
					onChange={onChange}
					checked={_checked.indexOf(+value) > -1}
					isStatic
					className={isEmpty && css.empty}
				/>
			))}
			{hidden.length > 0 && (
				<>
					<AnimatePresence initial={false}>
						{showAll && hidden.map(({ label, value, isEmpty = false }) => (
							<Checkbox
								name={name}
								label={label}
								value={+value}
								onChange={onChange}
								checked={_checked.indexOf(+value) > -1}
								key={`${name}_${value}`}
								className={isEmpty && css.empty}
							/>
						))}
					</AnimatePresence>
					<Button blue text small onClick={onShowAllClick}>
						{showAll ? '-' : '+'} Show {showAll ? 'fewer' : 'all'} {label}
					</Button>
				</>
			)}
		</div>
	);
}
